import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
// tslint:disable-next-line: no-submodule-imports
import { FaTrash } from 'react-icons/fa';

import { makePaginated } from '../../../../adl-gen/common';
import { DbKey } from '../../../../adl-gen/common/db';
import { Owner, RoleRecommendationMethod, WorkRole } from '../../../../adl-gen/totalinteraction/futurefit/db';
import { RoleBuilderData, WorkRoleListEntry } from '../../../../adl-gen/totalinteraction/futurefit/frontend';
import { WorkRoleReferenceData } from '../../../../adl-gen/totalinteraction/futurefit/requests';
import { LangCode, withI18n, WithI18nProps } from '../../../../internationalization/i18n-hoc';
import { PlotKeyToggleState } from '../../../../stores/plot-key-toggle-state';
import {
  EditIcon,
  PlusIcon
  //  PlusIcon,
} from '../../../../ui/common/icons';
import { Button } from '../../../../ui/widgets/button/button';
import { Select, SelectOption } from '../../../../ui/widgets/select/select';
import { RoleBuilderHelper } from '../../../stores';
import { DisplayI18nText, MultiSelectContainer, DisplayText } from '../../common/editor';
import { ListView } from '../../common/list/list-view';
import { QuizListEntry, WorkRoleQuizListView } from '../lists/quiz-list/quiz-list-view';
import { WorkRoleScoreRangeListView } from '../lists/workrole-list/workrole-score-range-list-view';

import { RoleBuilderSummary } from './role-builder-summary-view';

const styles = require('../../common/details-view.css');
const commonStyles = require('../../common/common.css');

interface WorkRoleDetailsViewProps extends WithI18nProps {
  workrole: WorkRoleListEntry;
  roleData: WorkRoleReferenceData;
  roleBuilderData: RoleBuilderData;
  onUpdate(workroleId: DbKey<WorkRole>): Promise<void>;
  onRoleBuilderUpdate(workroleId: DbKey<WorkRole>): void;
  onGenerateQuiz(workRoleId: DbKey<WorkRole>): void;
  onCloneWorkRole(workRoleId: DbKey<WorkRole>): void;
  onUpdateQuiz(quiz: QuizListEntry): Promise<void>;
  applicationRole: 'client-admin' | 'super';
}

type UpdateableQuizListEntry = QuizListEntry & { updated?: boolean };

/// Make observer for the LangSelector/WithI18n HOC
@observer
class WorkRoleDetailsView0 extends React.Component<WorkRoleDetailsViewProps> {
  /// Language management
  @observable lang: LangCode = this.props.currentLangCode();
  @observable quizList: UpdateableQuizListEntry[];

  constructor(props: Readonly<WorkRoleDetailsViewProps>) {
    super(props);
    this.quizList = this.workRoleQuizDataToQuizList();
  }

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <Select
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.lang)}
        onChange={onChange}
      />
    );
  });

  /// Sort this out later
  private readonly deleteAllowed: boolean = false;

  ownerToString = (owner: Owner): string => {
    if (owner.kind === 'appClientId') {
      const appClient = this.props.workrole.appClient;
      return appClient ? appClient.name : 'notfound';
    }
    return 'GLOBAL';
  };

  workRoleQuizDataToQuizList = (): UpdateableQuizListEntry[] => {
    return this.props.roleBuilderData.quizzes
      .map(q => q.quiz)
      .map(q => ({
        id: q.id,
        title: this.props.t(q.value.title),
        imageUrl: q.value.imageUrl,
        workRoleId: q.value.workRoleId,
        workRole: q.value.workRole,
        lockResults: q.value.lockResults
      }));
  };

  @action
  onToggleDeferQuizResults = (fieldName: string, item: QuizListEntry, value: boolean) => {
    const update = this.quizList.find(listItem => listItem.id === item.id);
    // TODO support other fields
    if (update && fieldName === 'lockResults') {
      update.lockResults = value;
      update.updated = true;
    }
  }

  onUpdateQuizzes = async () => {
    const { onUpdateQuiz } = this.props;
    await runInAction(async () => {
      for (const quiz of this.quizList) {
        if (quiz.updated) {
          // TODO support other fields
          delete quiz.updated;
          await onUpdateQuiz(quiz);
        }
      }
    })
  }

  Name = DisplayI18nText({
    field: this.props.workrole.name,
    lang: this.lang,
    title: 'Name',
    xsSpan: 2
  });

  Shortname = DisplayI18nText({
    field: this.props.workrole.shortname,
    lang: this.lang,
    title: 'Short Name',
    xsSpan: 2
  });

  renderProviders = () => {
    const { roleData, workrole } = this.props;
    const options = roleData.providerList.map(lp => ({ label: lp.value.name, value: lp.id }));
    const selectedOptions =
      workrole.recommendationProviders.length > 0
        ? options.filter(opt => workrole.recommendationProviders.includes(opt.value))
        : [{ label: 'None', value: '' }];
    return MultiSelectContainer({
      options,
      selectedOptions,
      title: 'Recommendation Providers',
      xsSpan: 4
    });
  };

  RecommendationMethod = DisplayText({
    field:
      this.props.workrole.recommendationMethod === RoleRecommendationMethod.relevance
        ? 'relevance'
        : 'role/competency/score',
    title: 'Recommendation Method',
    xsSpan: 2
  });
  // )

  Owner = DisplayText({
    field: this.ownerToString(this.props.workrole.owner),
    title: 'Owner',
    xsSpan: 2
  });

  /// TODO Replace all these divs with a flexbox and flex items
  renderTopButtons = () => {
    const { applicationRole, onCloneWorkRole, onUpdate, workrole } = this.props;
    const updateButton = () => {
      return (
        <div className={commonStyles.buttonList}>
          <Button
            onClick={async () => onUpdate(workrole.id)}
            Icon={EditIcon}
            size="small"
            variant="round"
            disabled={applicationRole === 'client-admin' && workrole.owner.kind === 'global'}
          >
            Update Details/Score Ranges
          </Button>
        </div>
      );
    };

    const saveAsButton = () => {
      return (
        <div className={commonStyles.buttonList}>
          <Button onClick={async () => onCloneWorkRole(workrole.id)} Icon={PlusIcon} size="small" variant="round">
            Clone/Save As...
          </Button>
        </div>
      );
    };

    const deleteButton = () => {
      return (
        <div className={commonStyles.buttonList}>
          <Button
            Icon={FaTrash}
            size="small"
            variant="round"
            disabled={applicationRole === 'client-admin' && workrole.owner.kind === 'global'}
          >
            Delete
          </Button>
        </div>
      );
    };

    return (
      <div className={commonStyles.buttonListContainer}>
        {this.deleteAllowed && deleteButton()}
        {saveAsButton()}
        {updateButton()}
      </div>
    );
  };

  renderRoleBuilderUpdateButton = () => {
    const { applicationRole, onRoleBuilderUpdate, workrole } = this.props;
    const updateButton = () => {
      return (
        <div className={commonStyles.buttonList}>
          <Button
            onClick={async () => onRoleBuilderUpdate(workrole.id)}
            Icon={EditIcon}
            size="small"
            variant="round"
            disabled={applicationRole === 'client-admin' && workrole.owner.kind === 'global'}
          >
            Update Expected Values
          </Button>
        </div>
      );
    };
    return <div className={commonStyles.buttonListContainer}>{updateButton()}</div>;
  };

  generateQuizButton = () => {
    const { applicationRole, onGenerateQuiz, workrole } = this.props;
    return (
        <div className={commonStyles.buttonList}>
          <Button
            onClick={async () => onGenerateQuiz(workrole.id)}
            Icon={PlusIcon}
            size="small"
            variant="round"
            disabled={applicationRole === 'client-admin' && workrole.owner.kind === 'global'}
          >
            Generate
          </Button>
        </div>
      );
  };

  updateQuizButton = () => {
    // TODO:
    // - handle more field updates
    // - Push quiz update processing into separate editor
    return (
      <div className={commonStyles.buttonList}>
        <Button
          onClick={async () => this.onUpdateQuizzes()}
          Icon={EditIcon}
          size="small"
          variant="round"
          disabled={this.quizList.every(item => item.updated === undefined)}
        >
          Update
        </Button>
      </div>
    );

  }

  renderQuizButtons = () => {
    return <div className={commonStyles.buttonListContainer}>
      {this.updateQuizButton()}
      {this.generateQuizButton()}
    </div>;

  }

  /// TODO: LangSelector isn't working, because the displayI18nText are not observers and not reacting to actions
  renderDetails = () => {
    const { t, workrole } = this.props;
    return (
      <React.Fragment>
        <div>
          <h4>{t(workrole.name)}</h4>
          {this.renderTopButtons()}
          {/* <this.LangSelector /> */}
        </div>
        <div className={styles.row}>
          {this.Name}
          {this.Shortname}
        </div>
        <div className={styles.row}>
          {this.Owner}
          {this.RecommendationMethod}
        </div>
        <div className={styles.row}>{this.renderProviders()}</div>
      </React.Fragment>
    );
  };

  renderScoreRanges = () => {
    const { scoreRanges } = this.props.workrole;
    const list = makePaginated({
      items: scoreRanges
        .slice()
        .sort((a, b) => a.value.scoreRange.range.v1 - b.value.scoreRange.range.v1)
        .map(item => ({ id: item.id, ...item.value })),
      current_offset: 0,
      total_size: scoreRanges.length
    });
    return <ListView list={list} fields={WorkRoleScoreRangeListView} />;
  };

  renderRoleBuilderView = () => {
    const { roleBuilderData, workrole } = this.props;

    const rb = RoleBuilderHelper.fromRoleBuilderData(workrole, roleBuilderData);
    // cf storeFactory.makePlotKeyToggleStore
    // TODO: This is meant to go into state and passed down through props - but otoh
    // TODO: we are not using plotKeys (yet - till we do role comparisons)
    const plotKeyToggleState: PlotKeyToggleState = new PlotKeyToggleState();
    // Hide "actual"
    plotKeyToggleState.toggle('actual');
    return <RoleBuilderSummary rb={rb} plotKeyToggleState={plotKeyToggleState} />;
  };

  renderQuizList = () => {
    const { roleBuilderData } = this.props;
    return (
      <div>
        <ListView
          list={makePaginated({ items: this.quizList, current_offset: 0, total_size: this.quizList.length })}
          fields={WorkRoleQuizListView}
          refData={roleBuilderData}
          onToggle={this.onToggleDeferQuizResults}
        />
      </div>
    );
  };

  render() {
    const { t, workrole } = this.props;
    return (
      <React.Fragment>
        <div className={styles.page}>
          <div className={styles.body}>{this.renderDetails()}</div>
          <div className={styles.body}>
            <div className={styles.row}>
              <h4>Score Ranges</h4>
              {this.renderScoreRanges()}
            </div>
          </div>
          <div className={styles.body}>
            <div className={styles.row}>
              <h4>{t(workrole.name)} - Expected Values</h4>
              {this.renderRoleBuilderUpdateButton()}
              {this.renderRoleBuilderView()}
            </div>
          </div>
          <div className={styles.body}>
            <div className={styles.row}>
              <h4>{t(workrole.name)} - Assessments</h4>
              {this.renderQuizButtons()}
              {this.renderQuizList()}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export const WorkRoleDetailsView = withI18n(WorkRoleDetailsView0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/detail-views/workrole-details-view.tsx